import { useEffect, useMemo, useRef, useState } from "react"

import { useDrag } from "react-dnd"

import { ArrowDownIcon, ArrowPathRoundedSquareIcon, ArrowTopRightOnSquareIcon, ArrowUpIcon, ExclamationCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { useQueryClient } from "@tanstack/react-query"

import Modal from "../../layouts/Modal"
import { DragTypes } from "../../models/DragTypes"
import { ILead, LeadStatus, PaymentStatus } from "../../models/Lead"
import { Distances } from "../../pages/Calendar/TripManager"
import Lead from "../../pages/Leads/Lead"
import classNames from "../../utils/classNames"
import useDriverTrackingLatest from "../../data/useDriverTrackingLatest"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faCreditCard, faHashtag, faRoad, faTriangleExclamation, faTruck, faHourglass } from "@fortawesome/free-solid-svg-icons"
import Tooltip from "../shared/Tooltip"
import { formatCollectionOption, hasCollectionOption } from "../../utils/leadPaymentOption"
import { getLeadPostcode } from "../../utils/getLeadLocation"

interface ILeadEventProps {
  lead: ILead
  selected?: ILead | undefined
  handleSelected?: any
  distances?: Distances | undefined
  inGroup: number | undefined
  handleOrderChange: any
  searchTerm?: string
}

export default function LeadEvent({ lead, selected, handleSelected, distances, inGroup, handleOrderChange, searchTerm }: ILeadEventProps) {
  const queryClient = useQueryClient();
  const { driverTrackingLatest } = useDriverTrackingLatest();
  const [open, setOpen] = useState(false);
  const prevState = usePrevious(open);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DragTypes.EVENT,
    item: lead,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }))

  function usePrevious(value: any) {
    const ref = useRef();

    useEffect(() => {
      ref.current = value;
    });

    return ref.current;
  }

  useEffect(() => {
    //After you close the lead modal, invalidate leads
    if (open === false && prevState === true) {
      queryClient.invalidateQueries(["leads"]);
    }
  }, [open])

  const handleColour = () => {
    let background = "bg-red-200"

    if (lead.completedOn !== null || lead.status === LeadStatus["Completed"]) background = "bg-green-300";

    if (lead.status === LeadStatus["Collected"]) background = "bg-orange-300";
    if (lead.status === LeadStatus["Cancelled"]) background = "bg-red-500";

    if (lead.id == selected?.id) background = "bg-blue-200";

    if (distances !== undefined) {
      background = "bg-gray-300";

      if (distances?.green?.includes(lead.id)) {
        background = "bg-green-500";
      } else if (distances?.amber?.includes(lead.id)) {
        background = "bg-yellow-500";
      } else if (distances?.red?.includes(lead.id)) {
        background = "bg-red-500";
      }
    }

    if (searchTerm !== undefined && searchTerm.length > 0) {
      if (lead.vehicle?.vrm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getLeadPostcode(lead).toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.contact?.postcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.contact?.fullname?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        background = "bg-yellow-200";
      }
    }

    if (lead.status === LeadStatus["Cancelled"]) background += " line-through";

    return background;
  }


  const isActive = useMemo(() => {
    if (driverTrackingLatest.data?.data) {
      const latestTracking = driverTrackingLatest.data.data.find(dt => dt.leadId === lead.id && dt.status === 1);
      return latestTracking !== undefined;
    }
    return false;
  }, [lead, driverTrackingLatest.data]);


  const buildIcons = () => {
    let icons = [];

    if (isActive) {
      icons.push(<Tooltip content="Pickup In Progress"><ArrowPathRoundedSquareIcon title="Pickup In Progress" className="h-5 w-5" /></Tooltip>);
    }

    if (lead.vehicle?.hasAlert) {
      icons.push(<Tooltip content="Vehicle Alert"><FontAwesomeIcon title="Vehicle Alert" icon={faTriangleExclamation} className="text-amber-500 text-lg" /></Tooltip>);
    }

    if (lead.vehicle?.isVan) {
      icons.push(<Tooltip content="Van"><FontAwesomeIcon title="Van" icon={faTruck} className="text-primary-600" /></Tooltip>);
    }

    if (lead.vehicle?.driveType === "4x4") {
      icons.push(<Tooltip content="4x4 Vehicle"><FontAwesomeIcon title="4x4 Vehicle" icon={faHashtag} className="text-primary-600 text-lg" /></Tooltip>);
    }

    if (hasCollectionOption(lead)) {
      const label = formatCollectionOption(lead);
      icons.push(<Tooltip content={label}><FontAwesomeIcon title={label} icon={faRoad} className="text-green-500 text-lg" /></Tooltip>);
    } else {
      if (lead.roadSidePayment) {
        icons.push(<Tooltip content="Roadside Payment"><FontAwesomeIcon title="Roadside Payment" icon={faRoad} className="text-green-500 text-lg" /></Tooltip>);
      }

      if (lead.fastPayment) {
        icons.push(<Tooltip content="Same Day Payment"><FontAwesomeIcon title="Same Day Payment" icon={faHourglass} className="text-green-500 text-lg" /></Tooltip>);
      }
    }

    const paymentStatusClass = (() => {
      switch (lead.paymentStatus) {
        case PaymentStatus.Raised:
          return "text-blue-500";
        case PaymentStatus.Sent:
          return "text-green-500";
        case PaymentStatus.Failed:
          return "text-red-500";
        case PaymentStatus.Approved:
          return "text-emerald-600";
        default:
          return "text-gray-500";
      }
    })();

    const paymentStatusLabel = PaymentStatus[lead.paymentStatus] ?? "Unknown";
    icons.push(
      <Tooltip content={`${paymentStatusLabel}`}>
        <FontAwesomeIcon
          title={`Payment Status: ${paymentStatusLabel}`}
          icon={faCreditCard}
          className={`${paymentStatusClass} text-lg`}
        />
      </Tooltip>
    );

    return icons;
  }


  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-6xl">
        <Lead modalLeadId={lead.id} setOpen={setOpen} />
      </Modal>

      <div
        className={classNames(handleColour(), " col-span-1 flex justify-between items-center mx-1 text-sm p-1 rounded-md mb-2 cursor-default")}
        ref={drag}
        onClick={() => handleSelected(lead)}
        style={{
          opacity: isDragging ? 0.8 : 1,
        }}
      >
        <div>
          <span className="flex gap-x-1 items-center">
            {buildIcons().map((icon, index) => (
              <span key={index} className="">
                {icon}
              </span>
            ))}
          </span>
          <span>
            {`${lead.vehicle?.vrm ?? ""}, ${getLeadPostcode(lead)} `}
          </span>
        </div>

        {/* <span>
          {lead.order}
        </span> */}

        <div className="flex">

          {(inGroup !== undefined && inGroup > 1) && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderChange(true, lead);
                }}
              >
                <ArrowUpIcon className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderChange(false, lead);
                }}
              >
                <ArrowDownIcon className="h-4 w-4" />
              </button>
            </>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
          </button>
        </div>
      </div >
    </>
  )
}